import { useEffect, useRef, useState } from "react";

/**
 * LazySection — defers rendering a below-the-fold section until it scrolls
 * near the viewport (or is already close on load). Keeps the homepage's
 * initial render + JS cheap on mobile; sections below the hero/marquee fold
 * mount their markup and start their images only when the visitor reaches them.
 *
 * `height` reserves layout space so content below doesn't jump when the
 * section mounts (min-height matches roughly what the section will occupy).
 */
export default function LazySection({ children, minHeight = 400 }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 0px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);

  return (
    <div ref={ref} style={show ? undefined : { minHeight }}>
      {show ? children : null}
    </div>
  );
}